import {
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type Control,
  type FieldErrors,
  type Path,
  useController,
  useWatch,
} from 'react-hook-form';
import {
  alpha,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchIcon from '@mui/icons-material/Search';

import { useSearchSampleID } from '@/api/sample/sample';
import { type SampleSearchResult } from '@/api/sample/types';
import { ProjectSchema, SampleSchema } from '@/zod_schemas';

import HelperTextContainer from '@/components/HelperTextContainer';
import SampleIDField from './SampleIDField';

interface SampleIDComponentProps {
  index: number;
  experimentIndex: number;
  errors: FieldErrors<ProjectSchema>;
  control: Control<ProjectSchema>;
  loadSampleData: (sample: SampleSchema | string) => void;
  clearSampleData: () => void;
}

const MINIMUM_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

const sampleTypeLabels: Record<SampleSchema['sample_type'], string> = {
  bam: 'BAM',
  paired_fastq: 'Paired FASTQ',
  unpaired_fastq: 'Unpaired FASTQ',
};

const formatUpdatedDate = (date?: string) => {
  if (!date) return null;

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return null;

  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(parsedDate);
};

const SampleIDComponent = ({
  index,
  experimentIndex,
  errors,
  control,
  loadSampleData,
  clearSampleData,
}: SampleIDComponentProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const samplePath: Path<ProjectSchema> = `experiments.${experimentIndex}.samples.${index}`;
  const dialogId = `sample-picker-${experimentIndex}-${index}`;
  const currentSample = useWatch({
    control,
    name: samplePath,
  });
  const { field } = useController({
    control,
    name: `${samplePath}.sample_id`,
  });
  const currentSampleId = currentSample?.sample_id || '';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const registerButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const trimmedSearch = searchValue.trim();
  const trimmedDebouncedSearch = debouncedSearch.trim();
  const searchIsSettled = trimmedSearch === trimmedDebouncedSearch;
  const isRecentQuery = trimmedDebouncedSearch.length === 0;
  const isSearchQuery = trimmedDebouncedSearch.length >= MINIMUM_SEARCH_LENGTH;
  const queryEnabled =
    dialogOpen && searchIsSettled && (isRecentQuery || isSearchQuery);

  const {
    data = [],
    isFetching,
    isError,
    refetch,
  } = useSearchSampleID(
    isSearchQuery ? trimmedDebouncedSearch : '',
    queryEnabled,
  );

  const visibleSamples = useMemo(
    () => (searchIsSettled && (isRecentQuery || isSearchQuery) ? data : []),
    [data, isRecentQuery, isSearchQuery, searchIsSettled],
  );
  const hasExactMatch = visibleSamples.some(
    (sample) =>
      sample.sample_id.toLocaleLowerCase() ===
      trimmedSearch.toLocaleLowerCase(),
  );
  const canRegister =
    searchIsSettled &&
    isSearchQuery &&
    !isFetching &&
    !isError &&
    trimmedSearch.length > 0 &&
    !hasExactMatch;
  const isLoading = dialogOpen && (!searchIsSettled || isFetching);
  const showMinimumSearchPrompt =
    searchIsSettled &&
    trimmedSearch.length > 0 &&
    trimmedSearch.length < MINIMUM_SEARCH_LENGTH;
  const showEmptyState =
    !isLoading &&
    !isError &&
    !showMinimumSearchPrompt &&
    visibleSamples.length === 0;
  const sampleErrors = errors.experiments?.[experimentIndex]?.samples?.[index];

  const handleOpen = () => {
    const initialSearch = currentSampleId.trim();
    setSearchValue(initialSearch);
    setDebouncedSearch(initialSearch);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
    searchInputRef.current?.focus();
  };

  const handleClearSelection = (closeDialog = false) => {
    field.onChange('');
    clearSampleData();
    setSearchValue('');
    setDebouncedSearch('');

    if (closeDialog) {
      handleClose();
    }
  };

  const handleSelectSample = (sample: SampleSearchResult) => {
    field.onChange(sample.sample_id);
    loadSampleData(sample);
    handleClose();
  };

  const handleRegisterSample = () => {
    if (!canRegister) return;

    const newSample: SampleSchema = {
      sample_id: trimmedSearch,
      isNew: true,
      gender: 'male',
      sample_type: 'bam',
      files: [{ file_path: 'string', file_type: 'bam' }],
    };
    field.onChange(trimmedSearch);
    loadSampleData(newSample);
    handleClose();
  };

  const focusFirstResult = () => {
    if (visibleSamples.length > 0) {
      optionRefs.current[0]?.focus();
      return;
    }

    if (canRegister) {
      registerButtonRef.current?.focus();
    }
  };

  const handleSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusFirstResult();
    }
  };

  const handleOptionKeyDown = (
    event: ReactKeyboardEvent<HTMLDivElement>,
    optionIndex: number,
  ) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextOption = optionRefs.current[optionIndex + 1];
      if (nextOption) {
        nextOption.focus();
      } else if (canRegister) {
        registerButtonRef.current?.focus();
      }
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const previousOption = optionRefs.current[optionIndex - 1];
      if (previousOption) {
        previousOption.focus();
      } else {
        searchInputRef.current?.focus();
      }
    }
  };

  const handleRegisterKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const lastOption = optionRefs.current[visibleSamples.length - 1];
      if (lastOption) {
        lastOption.focus();
      } else {
        searchInputRef.current?.focus();
      }
    }
  };

  return (
    <>
      <SampleIDField
        name={field.name}
        value={currentSampleId}
        error={Boolean(sampleErrors?.sample_id)}
        onBlur={field.onBlur}
        inputRef={field.ref}
        onOpen={handleOpen}
        onClear={() => handleClearSelection()}
        dialogOpen={dialogOpen}
        dialogId={dialogId}
      />
      <HelperTextContainer>
        <span>{sampleErrors?.sample_id?.message}</span>
      </HelperTextContainer>

      <Dialog
        id={dialogId}
        open={dialogOpen}
        onClose={handleClose}
        fullWidth
        fullScreen={fullScreen}
        maxWidth="sm"
        aria-labelledby={`${dialogId}-title`}
        PaperProps={{
          sx: {
            maxHeight: { sm: 'min(680px, calc(100vh - 64px))' },
            border: { sm: '1px solid' },
            borderColor: { sm: 'divider' },
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          id={`${dialogId}-title`}
          sx={{ pr: 7, pb: 1.5, position: 'relative' }}
        >
          <Typography component="span" variant="h3">
            Select a sample
          </Typography>
          <Typography
            component="p"
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Choose an active sample or register a new Sample ID.
          </Typography>
          <IconButton
            aria-label="close sample selection dialog"
            onClick={handleClose}
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 0, sm: 420 },
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, flexShrink: 0 }}>
            <TextField
              inputRef={searchInputRef}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              label="Search Sample ID"
              placeholder="Enter at least 2 characters"
              autoFocus
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {isLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      searchValue && (
                        <Tooltip title="Clear search">
                          <IconButton
                            size="small"
                            aria-label="clear sample search"
                            onClick={handleClearSearch}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isLoading && <LinearProgress />}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            {isError && searchIsSettled && (
              <Alert
                severity="error"
                sx={{ m: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => refetch()}
                  >
                    Retry
                  </Button>
                }
              >
                Samples could not be loaded. Please try again.
              </Alert>
            )}

            {showMinimumSearchPrompt && (
              <Stack alignItems="center" spacing={1} sx={{ px: 3, py: 5 }}>
                <SearchIcon color="disabled" />
                <Typography variant="subtitle2">
                  Continue typing to search
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter at least {MINIMUM_SEARCH_LENGTH} characters.
                </Typography>
              </Stack>
            )}

            {showEmptyState && (
              <Stack alignItems="center" spacing={1} sx={{ px: 3, py: 5 }}>
                <Inventory2OutlinedIcon color="disabled" />
                <Typography variant="subtitle2">
                  {isRecentQuery
                    ? 'No recent active samples'
                    : 'No existing samples found'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  {isRecentQuery
                    ? 'Search by Sample ID to find an existing sample.'
                    : `No active sample matches “${trimmedSearch}”.`}
                </Typography>
              </Stack>
            )}

            {!isError && visibleSamples.length > 0 && (
              <List disablePadding role="listbox" aria-label="Active samples">
                {visibleSamples.map((sample, sampleIndex) => {
                  const selected = Boolean(
                    currentSample?.id && sample.id === currentSample.id,
                  );
                  const updatedDate = formatUpdatedDate(
                    sample.updated_at || sample.created_at,
                  );

                  return (
                    <ListItemButton
                      key={sample.id || sample.sample_id}
                      ref={(node) => {
                        optionRefs.current[sampleIndex] = node;
                      }}
                      role="option"
                      aria-selected={selected}
                      selected={selected}
                      onClick={() => handleSelectSample(sample)}
                      onKeyDown={(event) =>
                        handleOptionKeyDown(event, sampleIndex)
                      }
                      sx={(currentTheme) => ({
                        minHeight: 66,
                        px: { xs: 2, sm: 2.5 },
                        py: 1.1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&.Mui-selected': {
                          bgcolor: alpha(
                            currentTheme.palette.primary.main,
                            currentTheme.palette.mode === 'dark' ? 0.2 : 0.1,
                          ),
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: alpha(
                            currentTheme.palette.primary.main,
                            currentTheme.palette.mode === 'dark' ? 0.27 : 0.15,
                          ),
                        },
                        '&.Mui-focusVisible': {
                          outline: `2px solid ${currentTheme.palette.primary.main}`,
                          outlineOffset: -2,
                        },
                      })}
                    >
                      <ListItemIcon sx={{ minWidth: 38 }}>
                        <Inventory2OutlinedIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        sx={{ minWidth: 0, mr: 1 }}
                        primary={
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {sample.sample_id}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{
                              display: 'block',
                              textTransform: 'capitalize',
                            }}
                          >
                            {sampleTypeLabels[sample.sample_type]} ·{' '}
                            {sample.gender}
                            {updatedDate ? ` · Updated ${updatedDate}` : ''}
                          </Typography>
                        }
                      />
                      {selected ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          color="primary.main"
                        >
                          <CheckCircleOutlineIcon fontSize="small" />
                          <Typography variant="caption" fontWeight={700}>
                            Selected
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography
                          variant="caption"
                          color="primary.main"
                          fontWeight={700}
                        >
                          Select
                        </Typography>
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>

          {canRegister && (
            <Box
              sx={{
                p: 2,
                flexShrink: 0,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Button
                ref={registerButtonRef}
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleRegisterSample}
                onKeyDown={handleRegisterKeyDown}
                fullWidth
                sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
              >
                Register new sample “{trimmedSearch}”
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.75 }}
              >
                The new sample record will be created when the project is saved.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            justifyContent: currentSampleId ? 'space-between' : 'flex-end',
          }}
        >
          {currentSampleId && (
            <Button color="inherit" onClick={() => handleClearSelection(true)}>
              Clear selection
            </Button>
          )}
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SampleIDComponent;
